// version 1
import React, {
  Dispatch,
  ReactNode,
  useMemo,
  useState,
  SetStateAction,
} from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableSortLabel,
  TableRow,
  Menu,
  MenuItem,
  IconButton,
  Skeleton,
  Button,
  Typography,
  Box,
  TablePagination,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  TextField,
  styled,
  createSvgIcon,
  alpha,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { IoDocumentTextOutline } from "react-icons/io5";
import { PiDotsThreeCircleVerticalLight } from "react-icons/pi";
import { FilterSearchText } from "../../Components/Filter/FilterSeacrchText";
import { WSIcons } from "../../commonFunctions/iIcons";
export interface Column {
  field: string;
  label: string;
  sortable?: boolean;
  isDate?: boolean;
  type?: "number" | "string" | "date" | "boolean" | "status" | "currency";
  render?: (item: any) => ReactNode;
}

export interface Action {
  label: string;
  handler: (item: any) => void;
}

export interface DataTableProps {
  data: any[];
  loading: boolean;
  columns: Column[];
  actions?: Action[];
  filters?: {
    options: {
      value: string;
      label: string;
    }[];
    value: string
    defaultValue?: string;
    onChange: (value: string) => void;
    customDateRange?: {
      fromDate: string | null;
      toDate: string | null;
      onFromDateChange: (date: string) => void;
      onToDateChange: (date: string) => void;
    };
  };
  sort?: {
    options: {
      value: string;
      label: string;
    }[];
    defaultValue?: string;
    value: string;
    onChange: (value: string) => void;
  };
  search?: {
    fieldName: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
  };
  page: number;
  setPage?: Dispatch<SetStateAction<number>>;
  onPageChange: (newPage: number) => void;
  totalPages: number;
  rowsPerPage: number;
  setRowsPerPage: (rows: number) => void;
  getLink?: (item: any) => string;
  exportToExcel?: boolean;
  padding?: boolean;
  title?: string;
}

const ExportIcon = createSvgIcon(
  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
  'SaveAlt',
);

const DataTable = ({
  data,
  loading,
  title,
  columns,
  filters,
  sort,
  search,
  actions = [],
  page,
  onPageChange,
  setPage,
  totalPages,
  rowsPerPage,
  setRowsPerPage,
  getLink,
  exportToExcel = false,
  padding = false,
}: DataTableProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");
  const [newFilters, setFilters] = useState(filters);
  const [filterOption, setFilterOption] = useState<string>("today");
  const [sortOption, setSortOption] = useState("created_desc");

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  console.log("data in table", data);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: any
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDownloadExcel = () => {
    if (!data || data.length === 0) return;

    const excelData = data.map((item) => {
      const row: Record<string, any> = {};
      columns.forEach((col) => {
        const value = getNestedValue(item, col.field);
        row[col.label] =
          col.type === "date" && dayjs(value).isValid()
            ? dayjs(value).format("DD/MM/YYYY")
            : value;
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `data-table-export-${Date.now()}.xlsx`);
  };

  const getNestedValue = (obj: any, path: string) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, orderBy);
      const bValue = getNestedValue(b, orderBy);

      if (aValue < bValue) return orderDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return orderDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, orderBy, orderDirection]);

  const getStatusChipColor = (status: string | boolean) => {
    const value = typeof status === "string" ? status.toLowerCase() : status;

    if (value === true) {
      return { label: "Active", color: "#4caf50" }; // Green
    }

    if (value === false) {
      return { label: "Inactive", color: "#f44336" }; // Red
    }

    switch (value) {
      case "pending":
        return { label: "Pending", color: "#ff9800" };
      case "confirmed":
        return { label: "Confirmed", color: "#2196f3" };
      case "completed":
        return { label: "Completed", color: "#2E8B57" };
      case "delivered":
        return { label: "Delivered", color: "#4caf50" };
      case "canceled":
        return { label: "Canceled", color: "#f44336" };
      case "outofstock":
      case "out of stock":
        return { label: "Out of Stock", color: "#9e9e9e" };
      case "instock":
      case "in stock":
        return { label: "In Stock", color: "#4caf50" };
      case "lowstock":
      case "low stock":
        return { label: "Low Stock", color: "#ffc107" };
      default:
        return {
          label: typeof status === "string" ? status : String(status),
          color: "#607d8b",
        };
    }
  };

  const GradientSelect = styled(Select)(() => ({
    height: '40px',
    px: 2,
    fontSize: '0.85rem',
    textTransform: 'none',
    background: 'white',
    border: '2px solid transparent',
    borderRadius: '8px',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    color: 'transparent',
    paddingLeft: "8px",
    display: "flex",
    alignItems: "center",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      color: "transparent",
      background: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontWeight: 500,
    },
    "& fieldset": {
      border: "none",
    },
  }));

  const GradientTextField = styled(TextField)(({ theme }) => ({
    border: "1px solid",
    borderImageSource: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
    borderImageSlice: 1,
    borderRadius: "8px",
    height: 40,
    paddingLeft: "8px",
    display: "flex",
    alignItems: "center",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      color: "transparent",
      background: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontWeight: 500,
    },
    "& fieldset": {
      border: "none",
    },
  }));

  return (
    <Paper sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: title ? "space-between" : "flex-end",
          alignItems: "center",
          p: 2,
          gap: 3,
        }}
      >
        {/* Title */}
        {title && (
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "1.15rem" }}>
            {title}
          </Typography>
        )}

        {/* Search */}
        {search && (
          <TextField
            label={search.placeholder}
            variant="outlined"
            onChange={search.onChange}
            fullWidth
            sx={{
              flex: 1,
              backgroundColor: (theme) => alpha(theme.palette.grey[50], 0.9),
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                height: '40px', // Match button height
                borderRadius: 2,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#bbb',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4A00E0',
                  borderWidth: 2,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Filters */}
        {filters && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <GradientSelect
              label="Filter"
              value={filters.value}
              onChange={(e) => filters.onChange(e.target.value as string)}
            >
              {filters.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </GradientSelect>
          </FormControl>
        )}

        {/* Custom Date Range */}
        {filters?.customDateRange && (
          <Box sx={{ display: "flex", gap: 2 }}>
            <GradientTextField
              type="date"
              label="From"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.customDateRange.fromDate || ""}
              onChange={(e) => filters.customDateRange?.onFromDateChange(e.target.value)}
            />
            <GradientTextField
              type="date"
              label="To"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.customDateRange.toDate || ""}
              onChange={(e) => filters.customDateRange?.onToDateChange(e.target.value)}
            />
          </Box>
        )}

        {/* Sort */}
        {sort && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <GradientSelect
              label="Sort By"
              value={sort.value}
              onChange={(e) => sort.onChange(e.target.value as string)}
            >
              {sort.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </GradientSelect>
          </FormControl>
        )}

        {/* Export */}
        {exportToExcel && (
          <Button
            onClick={handleDownloadExcel}
            startIcon={<ExportIcon sx={{ color: '#4AD16E' }} />}
            sx={{
              height: '40px',
              px: 2,
              fontSize: '0.85rem',
              textTransform: 'none',
              background: 'white',
              border: '2px solid transparent',
              borderRadius: '8px',
              backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: 'transparent',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
              }
            }}
          >
            <Box
              component="span"
              sx={{
                backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {'Export to Excel'}
            </Box>
          </Button>
        )}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table
          sx={{ minWidth: { xs: "100%", md: 600 } }}
          aria-labelledby="tableTitle"
          size={dense ? "small" : "medium"}
        >
          <TableHead>
            <TableRow
              sx={{
                // backgroundImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
                backgroundColor: "#E2E2E2",
              }}
            >
              {columns.map((column, index) => (
                <TableCell key={index} sx={{ fontWeight: 600, color: "#022324" }}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? orderDirection : "asc"}
                      onClick={() => handleRequestSort(column.field)}
                      sx={{
                        color: "inherit",
                        "&.Mui-active": {
                          color: "inherit",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell align="right" sx={{ fontWeight: 600, color: "#022324"}}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {columns.map((col, i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell align="right">
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : sortedData.length > 0 ? (
              sortedData.map((item) => (
                <TableRow
                  hover
                  key={item._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: getLink ? "#f0f8ff" : "#f9f9f9",
                    },
                    cursor: getLink ? "pointer" : "default",
                  }}
                >
                  {columns.map((col, i) => {
                    const value = getNestedValue(item, col.field);

                    return (
                      <TableCell
                        key={col.field || i}
                        onClick={() => {
                          if (getLink) navigate(getLink(item));
                        }}
                      >
                        {col.type === "boolean" ? (
                          <Chip
                            label={value ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              backgroundColor: value ? "#4caf50" : "#f44336",
                              color: "#fff",
                              fontWeight: 500,
                            }}
                          />
                        ) : col.type === "status" ? (
                          (() => {
                            const { label, color } = getStatusChipColor(value);
                            return (
                              <Chip
                                label={label}
                                size="small"
                                sx={{
                                  backgroundColor: color,
                                  color: "#fff",
                                  fontWeight: 600,
                                  borderRadius: "8px",
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: "0.75rem",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  textTransform: "capitalize",
                                  letterSpacing: 0.5,
                                  "& .MuiChip-label": {
                                    display: "flex",
                                    alignItems: "center",
                                  },
                                }}
                              />
                            );
                          })()
                        ) : col.type === "date" ? (
                          dayjs(value).isValid() ? (
                            dayjs(value).format("DD/MM/YYYY")
                          ) : (
                            "Invalid Date"
                          )
                        ) : col.type === "number" ? (
                          <span
                            style={{
                              textAlign: "right",
                              display: "inline-block",
                              minWidth: 60,
                            }}
                          >
                            {Number(value).toLocaleString()}
                          </span>
                        ) : col.type === "currency" ? (
                          <Typography style={{ fontSize: "0.875rem" }}>
                            ₹{value}
                          </Typography>
                        ) : col.render ? (
                          col.render(item)
                        ) : (
                          <Tooltip title={value || ""}>
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: 150,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#333",
                                fontSize: "0.95rem",
                              }}
                            >
                              {value}
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}

                  {actions.length > 0 && (
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) => handleMenuClick(event, item)}
                      >
                        <PiDotsThreeCircleVerticalLight />{" "}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    sx={{ py: 3 }}
                  >
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={totalPages * rowsPerPage}
        page={page - 1}
        onPageChange={(event, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          onPageChange(1); // reset to page 1 when limit changes
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {actions.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {actions.map((action) => (
            <MenuItem
              key={action.label}
              onClick={() => {
                action.handler(selectedItem);
                handleMenuClose();
              }}
              sx={{ fontSize: "14px" }}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      )}
      {padding && (
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
      )}
    </Paper>
  );
};

export default DataTable;
